import {getAwpClientHeaders} from '../../session/awpRequestHeaders';
import {API_BASE} from './reports';
import {isLocalEntityRef} from '../entityLinks';

function parseNonNegInt(value) {
    const n = Number(value);
    return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Compact mention counts for pickers / Spotlight. Omits zeros.
 * @param {{documentCount?: number|null, dataItemCount?: number|null, reportCount?: number|null}|null|undefined} entity
 * @returns {string}
 */
export function formatEntityMentionCounts(entity) {
    const docs = Number(entity?.documentCount);
    const items = Number(entity?.dataItemCount);
    const products = Number(entity?.reportCount);
    const parts = [];
    if (Number.isFinite(docs) && docs > 0) {
        parts.push(`${docs.toLocaleString()} doc${docs !== 1 ? 's' : ''}`);
    }
    if (Number.isFinite(items) && items > 0) {
        parts.push(`${items.toLocaleString()} item${items !== 1 ? 's' : ''}`);
    }
    if (Number.isFinite(products) && products > 0) {
        parts.push(`${products.toLocaleString()} product${products !== 1 ? 's' : ''}`);
    }
    return parts.join(' · ');
}

/**
 * Normalize a unified KB search hit for all entity pickers.
 * Accepts the planned API shape plus common aliases.
 *
 * @param {Object} raw
 * @returns {{
 *   entityRef: string,
 *   canonicalName: string,
 *   entityType: string|null,
 *   local: boolean,
 *   imageUrl: string|null,
 *   description: string|null,
 *   documentCount: number|null,
 *   reportCount: number|null,
 *   dataItemCount: number|null,
 *   entityId: string,
 *   entityLabel: string,
 *   id: string,
 *   label: string,
 * }|null}
 */
export function normalizeKbSearchHit(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const entityRef = String(
        raw.entityRef || raw.entityId || raw.wikidataId || raw.id || '',
    ).trim();
    if (!entityRef) return null;

    const canonicalName = String(
        raw.canonicalName || raw.entityLabel || raw.label || entityRef,
    ).trim() || entityRef;
    const local = raw.local === true
        || isLocalEntityRef(entityRef)
        || String(raw.source || '').toLowerCase() === 'local';

    return {
        entityRef,
        canonicalName,
        entityType: raw.entityType || null,
        local,
        imageUrl: raw.imageUrl || raw.image || null,
        description: raw.description || null,
        documentCount: parseNonNegInt(raw.documentCount),
        reportCount: parseNonNegInt(raw.reportCount),
        dataItemCount: parseNonNegInt(raw.dataItemCount),
        draftCandidate: raw.draftCandidate === true,
        // Compat aliases for existing pickers / dialogs.
        entityId: entityRef,
        entityLabel: canonicalName,
        id: entityRef,
        label: canonicalName,
    };
}

/**
 * Unified KB entity search (custom D + optional Wikidata).
 * GET /api/entities/search?q=&limit=&preferWikidata=
 * Spotlight omits preferWikidata so results stay local KB only.
 *
 * @param {string} query
 * @param {number} [limit=20]
 * @param {{preferWikidata?: boolean}} [options]
 * @returns {Promise<{results: ReturnType<typeof normalizeKbSearchHit>[]}>}
 */
export async function searchKbEntities(query, limit = 20, options = {}) {
    const q = (query || '').trim();
    if (q.length < 2) {
        return {results: []};
    }

    const url = new URL(`${API_BASE}/entities/search`);
    url.searchParams.set('q', q);
    url.searchParams.set('limit', String(limit));
    if (options?.preferWikidata) {
        url.searchParams.set('preferWikidata', 'true');
    }

    const res = await fetch(url.toString(), {headers: getAwpClientHeaders()});
    if (!res.ok) {
        throw new Error(`Entity search failed: HTTP ${res.status}`);
    }
    const json = await res.json();
    const rawResults = Array.isArray(json?.results)
        ? json.results
        : (Array.isArray(json) ? json : []);
    const results = rawResults
        .map(normalizeKbSearchHit)
        .filter(Boolean);
    return {results};
}

/**
 * Browse top Knowledge Base entities (occurrence-ranked).
 * GET /api/entities/kb?limit=
 *
 * @param {number} [limit=50]
 * @returns {Promise<ReturnType<typeof normalizeKbSearchHit>[]>}
 */
export async function listKbEntities(limit = 50) {
    const url = new URL(`${API_BASE}/entities/kb`);
    url.searchParams.set('limit', String(Math.max(1, Math.min(limit, 200))));

    const res = await fetch(url.toString(), {headers: getAwpClientHeaders()});
    if (!res.ok) {
        throw new Error(`KB entity list failed: HTTP ${res.status}`);
    }
    const json = await res.json();
    const raw = Array.isArray(json?.entities) ? json.entities : [];
    return raw
        .map((row) => {
            if (!row || typeof row !== 'object') return null;
            const wd = String(row.wikidataId || '').trim();
            const numericId = row.entityId;
            const entityRef = wd
                || (numericId != null && numericId !== '' ? `D${numericId}` : '');
            if (!entityRef) return null;
            return normalizeKbSearchHit({
                ...row,
                entityRef,
                local: true,
            });
        })
        .filter(Boolean);
}
